import IconComic from "@assets/images/icon-comic.svg?react";
import type { Route } from "@route-types/Article/+types/Article";
import Taboola from "@src/app/components/_ads/Taboola/Taboola";
import { TaboolaLinks } from "@src/app/components/_ads/Taboola/Taboola";
import styles from "@src/app/components/Article/Article.css?url";
import ArticleAuthorBylineSection from "@src/app/components/Article/ArticleAuthorBylineSection";
import ArticleBody from "@src/app/components/Article/ArticleBody";
import ArticleHeaderSection from "@src/app/components/Article/ArticleHeaderSection";
import ArticleMainMedia from "@src/app/components/Article/ArticleMainMedia";
import ArticlePopularReads from "@src/app/components/Article/ArticlePopularReads";
import ArticleRecommend from "@src/app/components/Article/ArticleRecommend";
import ArticleTags from "@src/app/components/Article/ArticleTags";
import NewsletterSignUp from "@src/app/components/NewsletterSignUp";
import { SocialShareLinks } from "@src/app/components/SocialShare/SocialShare";
import StickyCard from "@src/app/components/StickyCard/StickyCard";
import StickyContainer from "@src/app/components/StickyContainer/StickyContainer";
import {
  parsePayload,
  useRouteResponse,
} from "@src/app/hooks/useRouteResponse";
import viewportState from "@src/app/stores/viewport";
import type { ArticlePageResponse } from "@src/app/types/response";
import { createMetaTags } from "@src/app/utils/createMetaTags";
import cx from "clsx";
import { useEffect, useRef, useState } from "react";
import type { LinksFunction } from "react-router";
import { useSnapshot } from "valtio";

import { fetchArticlePageData } from "./Article.server";

export const loader = fetchArticlePageData;

export const meta = (args: Route.MetaArgs) => {
  const payload = parsePayload<ArticlePageResponse>(args.loaderData);
  const article = payload.article;
  const { metaTags, ogTags } = article || {};

  const title = metaTags?.title || `${article?.title} | ThinkChina`;
  const description =
    article?.standfirst ||
    article?.teaser ||
    `${article?.title} - Get latest insights on China through the eyes of global thought leaders & journalists.`;
  return createMetaTags({
    title,
    description: metaTags?.description || description || "",
    canonical:
      metaTags?.canonical ||
      `https://www.thinkchina.sg${article?.urlPath || ""}`,
    section: article?.section || "",
    author: article?.authors[0]?.name || "",
    keywords: (article?.tags || []).map((i) => i.name),
    robots: "index, follow, max-image-preview:large",
    ogInfo: {
      ogDescription: ogTags?.description || description || "",
      ogTitle: ogTags?.title || article?.title || "",
      ogType: "article",
      ogImage: ogTags?.image || "",
    },
  });
};

export const links: LinksFunction = () => {
  return [
    ...SocialShareLinks(),
    ...TaboolaLinks(),
    { rel: "stylesheet", href: styles },
  ];
};

/**
 * stg test url
 * bgread: /politics/update-politics07021345jqr-test-photo-story
 * normal: /politics/why-has-batams-rempang-eco-city-national-project-become-controversy
 * comic: /cartoon/comic-even-if-red-sun-still-rises-tomorrow-chinese-youths-reflection-chinas-covid-fight
 */
export default function ArticlePage() {
  const res = useRouteResponse("articlePage");
  const article = res.article;

  const articleBodyContainerRef = useRef<HTMLDivElement | null>(null);
  const articleAuthorBylineSectionRef = useRef<HTMLDivElement | null>(null);
  const taboolaRef = useRef<HTMLDivElement | null>(null);

  const [stickyMinHeight, setStickyMinHeight] = useState(0);

  const { isMobileView } = useSnapshot(viewportState);

  // feature & comic article & regional article
  const isComicArticle = article?.displayType == "comic";
  const isCartoonArticle = article?.section == "cartoon";
  const isBigReadArticle = article?.displayType == "big_read";
  const isPhotoStoryArticle = article?.displayType == "photo_story";

  const isRegionalArticle = article?.tags.some(
    (tag) => tag.urlPath === "/tags/thinkchina-regional-series",
  );

  const isFeaturedArticle = isBigReadArticle || isPhotoStoryArticle;
  const isFullWidthArticle =
    isFeaturedArticle || isComicArticle || isCartoonArticle;

  // set newsletter variant based on article type
  const newsletterVariant = isRegionalArticle
    ? "regionalCard"
    : article?.section === "culture"
      ? "cultureCard"
      : "card";

  // Recalculate sticky height whenever sizes change
  useEffect(() => {
    // container height use to deduct for sticky container
    const articleBodyEl = articleBodyContainerRef.current;
    const authorCardsEl = articleAuthorBylineSectionRef.current;
    const taboolaEl = taboolaRef.current;

    if (!articleBodyEl || !authorCardsEl || !taboolaEl) return;

    function updateStickyHeight(
      articleEl: HTMLDivElement,
      authorEl: HTMLDivElement,
      taboolaEl: HTMLDivElement,
    ) {
      const newHeight =
        articleEl.offsetHeight - authorEl.offsetHeight - taboolaEl.offsetHeight;
      setStickyMinHeight(Math.max(newHeight, 0));
    }

    // Observe all three containers
    const observer = new ResizeObserver(() => {
      updateStickyHeight(articleBodyEl, authorCardsEl, taboolaEl);
    });

    observer.observe(articleBodyEl);
    observer.observe(authorCardsEl);
    observer.observe(taboolaEl);

    // initial calculation
    updateStickyHeight(articleBodyEl, authorCardsEl, taboolaEl);

    return () => {
      observer.disconnect();
    };
  }, [articleBodyContainerRef, articleAuthorBylineSectionRef, taboolaRef]);

  return (
    <div
      ref={articleBodyContainerRef}
      className={cx(
        "article container mx-auto max-w-300",
        isFullWidthArticle ? "" : "grid grid-cols-12",
      )}
    >
      {/* Article Content */}
      <div
        className={cx(
          "mx-auto",
          isFullWidthArticle
            ? "lg:max-w-204"
            : "col-span-12 w-screen lg:col-span-9 lg:w-auto",
        )}
      >
        <ArticleHeaderSection />

        <div className={cx(isFullWidthArticle ? "" : "lg:hidden")}>
          <ArticleAuthorBylineSection />
        </div>

        {/* comic article tip */}
        {isComicArticle ? (
          <div className="pb-4">
            <div className="text-secondary tex-[14px] bg-background-secondary flex items-center justify-center gap-2 py-4 leading-4.5 font-bold">
              <IconComic width={24} height={24} />
              Click on an image to enter panel view
            </div>
          </div>
        ) : null}

        {/* TODO should set full width 1200px */}
        {isComicArticle ? null : <ArticleMainMedia />}

        <ArticleBody />

        <ArticleTags />

        {/* here NewsletterSignUp only exists in  */}
        {isFeaturedArticle ? (
          <NewsletterSignUp
            variant={isMobileView ? "card" : "default"}
            withIcon={isMobileView ? false : true}
          />
        ) : null}

        {isComicArticle ? null : (
          <div className="block lg:hidden">
            <ArticlePopularReads />
          </div>
        )}

        <ArticleRecommend size={isFullWidthArticle ? 4 : 3} />

        <div className="px-3">
          <Taboola
            ref={taboolaRef}
            articleDetailId={article?.id}
            urlPath={article?.urlPath || ""}
          />
        </div>
      </div>

      {/* Side Bar */}
      {isFullWidthArticle ? null : (
        <div className="hidden gap-y-12.5 lg:col-span-3 lg:flex lg:flex-col">
          <ArticleAuthorBylineSection ref={articleAuthorBylineSectionRef} />

          <StickyContainer minHeight={stickyMinHeight}>
            <StickyCard>
              <NewsletterSignUp variant={newsletterVariant} withIcon={false} />
            </StickyCard>

            <StickyCard>
              <ArticlePopularReads />
            </StickyCard>
          </StickyContainer>
        </div>
      )}
    </div>
  );
}
